import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { updateUserConfigurations } from '~/common/services/controllers/user';
import { useAuth } from '~/common/providers/auth';
import { IFCWithChildren } from '~/common/interfaces/i-fc-with-children';

import { compareVersions } from '../utils/compare-versions';
import { currentVersion } from '../data';
import { ReleaseNotesModal } from '../components/ReleaseNotesModal';

interface IReleaseNotesContextData {
  readonly isOpen: boolean;
  readonly openReleaseNotes: () => void;
  readonly closeReleaseNotes: () => void;
}

const ReleaseNotesContext = createContext<IReleaseNotesContextData>(
  {} as IReleaseNotesContextData
);

const ReleaseNotesProvider: IFCWithChildren = ({ children }) => {
  const { user, patchUserConfigurations } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  // Só a abertura automática marca a versão como vista — reabrir pelo rodapé da
  // sidebar é consulta, não deve disparar PATCH.
  const wasAutoOpenedRef = useRef(false);
  // Guarda o id do usuário já avaliado para o disparo acontecer uma vez por
  // sessão, mas voltar a acontecer se outro usuário entrar sem recarregar.
  const checkedUserIdRef = useRef<string | null>(null);

  // O disparo observa `user` em vez do onSuccess do login porque a sessão
  // também é reidratada pelo refresh token ao recarregar a página, e nesses
  // casos o login nunca é chamado.
  useEffect(() => {
    if (!user?.id) {
      checkedUserIdRef.current = null;
      return;
    }

    if (checkedUserIdRef.current === user.id) {
      return;
    }

    checkedUserIdRef.current = user.id;

    if (!currentVersion) {
      return;
    }

    const lastSeenVersion = user.configurations?.ultimaVersaoNotasVista;

    if (compareVersions(currentVersion, lastSeenVersion) > 0) {
      wasAutoOpenedRef.current = true;
      setIsOpen(true);
    }
  }, [user]);

  const openReleaseNotes = useCallback(() => {
    wasAutoOpenedRef.current = false;
    setIsOpen(true);
  }, []);

  const closeReleaseNotes = useCallback(() => {
    setIsOpen(false);

    if (!wasAutoOpenedRef.current || !user?.id) {
      return;
    }

    wasAutoOpenedRef.current = false;

    // Atualização otimista: o estado local muda na hora para o modal não
    // reabrir ao trocar de layout, e a falha do PATCH é silenciosa — no pior
    // caso as novidades reaparecem no próximo acesso.
    patchUserConfigurations({ ultimaVersaoNotasVista: currentVersion });

    updateUserConfigurations(
      { configuracoes: { ultimaVersaoNotasVista: currentVersion } },
      user.id
    ).catch(() => undefined);
  }, [patchUserConfigurations, user?.id]);

  const contextData = useMemo(
    () => ({
      isOpen,
      openReleaseNotes,
      closeReleaseNotes,
    }),
    [isOpen, openReleaseNotes, closeReleaseNotes]
  );

  return (
    <ReleaseNotesContext.Provider value={contextData}>
      <ReleaseNotesModal isOpen={isOpen} onClose={closeReleaseNotes} />
      {children}
    </ReleaseNotesContext.Provider>
  );
};

const useReleaseNotes = (): IReleaseNotesContextData =>
  useContext(ReleaseNotesContext);

export { ReleaseNotesProvider, useReleaseNotes };
